import React from 'react';

import { useRovingTabIndex } from '@lumapps/lumx/react/utils';

import { useUpdatedRef } from './useUpdatedRef';

export interface UseOptionOptions {
    /** Ref of the "option" element */
    ref: React.RefObject<HTMLElement>;
    /** Callback on option selected  */
    selectItem?(): void;
    /** Option is disabled */
    isDisabled?: boolean;
    /** Should the option be selected when it gains focus via the roving tabIndex */
    selectOnFocus?: boolean;
}

/** Props to apply to the "option" */
export interface UseOptionsOutput {
    tabIndex: number;
    onClick(): void;
    onKeyDown(event: React.KeyboardEvent): void;
}

/**
 * Implement ARIA role "option" accessibility state (tabIndex) and behavior (keydown, click).
 */
export const useOption = (options: UseOptionOptions): UseOptionsOutput => {
    const { ref, selectItem, isDisabled, selectOnFocus } = options;
    const [tabIndex, focused, rovingTabindexKeyDown, rovingTabindexClick] = useRovingTabIndex(ref, isDisabled);

    // Wrap in a ref to avoid un-necessary update
    const selectOnFocusRef = useUpdatedRef(selectOnFocus ? selectItem : undefined);

    // Focus on roving tab index update
    React.useEffect(() => {
        if (!focused || isDisabled) {
            return;
        }
        ref.current?.focus();

        // Select on focus
        selectOnFocusRef.current?.();
    }, [focused, isDisabled, ref, selectOnFocusRef]);

    return React.useMemo(
        () => ({
            tabIndex,
            onClick() {
                if (isDisabled) {
                    return;
                }
                // ARIA roving tab index: focus on click.
                rovingTabindexClick();

                if (!selectOnFocus) {
                    selectItem?.();
                }
            },
            onKeyDown(event) {
                if (!isDisabled && (event.key === ' ' || event.key === 'Enter')) {
                    // ARIA radio: select on space or enter key pressed.
                    selectItem?.();
                    event.preventDefault();
                } else {
                    // ARIA roving tab index: keyboard navigation
                    rovingTabindexKeyDown(event);
                }
            },
        }),
        [tabIndex, isDisabled, rovingTabindexClick, selectOnFocus, selectItem, rovingTabindexKeyDown],
    );
};
